import { selectColumn } from "components/BaseTableV3/selectColumn";
import ACL_RELATIONSHIPS from "acl-relationships";
import TagList from "components/TagList";
import { ScrollColumn } from "components/BaseTableV3/ScrollColumn";
import { TableLink } from "components/BaseTableV3/TableLink";
import RuvixxDate from "components/RuvixxDate";
import withRoleCheck from "components/hocs/withRoleCheck";
import FormResponsesListModal from "routes/campaign/components/FormResponsesListModal";
import _CampaignStatusModal from "routes/campaign/components/CampaignStatusModal";
import { CAMPAIGN_TARGET_TABLE_CONSTANTS } from "routes/campaign/components/campaign_target_table/CampaignTargetTableV3";
import { CampaignTargetTableActions } from "routes/campaign/components/campaign_target_table/CampaignTargetTableActions";
import { getUsersForFiltersQuery } from "helpers/user";
import EntityService from "services/Entity";
import {
  NEGATABLE_OPERATORS,
  NULLABLE_OPERATORS,
} from "components/NewAdvancedSearch/filters/selectOperators";

const CampaignStatusModal = withRoleCheck(_CampaignStatusModal, [
  ACL_RELATIONSHIPS.campaignTargetChangeStatus.edit,
]);

export const getCampaignTargetsTableColumns = ({
  campaignId = null,
  campaign = null,
  invalidateData = null,
  deleteCampaignTarget = null,
}) => {
  return [
    selectColumn,
    {
      header: "ID",
      id: "ID",
      accessorKey: "id",
      size: 60,
      cell: ({ row }) => (
        <TableLink
          href={`/campaigns/${campaignId}/target/${row.original.entity_id}`}
        >
          {row.original.entity_id}
        </TableLink>
      ),
    },
    {
      header: "Tags",
      id: "Tags",
      accessorKey: "tags",
      enableSorting: false,
      cell: ({ row }) => (
        <TagList
          tags={row.original.tags}
          modelType={CAMPAIGN_TARGET_TABLE_CONSTANTS.className}
          modelId={row.original.entity_id}
          onUpdate={invalidateData}
          tableCell
        />
      ),
    },
    {
      header: "Entity",
      id: "Entity",
      accessorKey: "entity_name",
      cell: ({ row }) => (
        <TableLink
          href={`/campaigns/${campaignId}/target/${row.original.entity_id}`}
        >
          {row.original.entity_name}
        </TableLink>
      ),
    },
    {
      header: "Entity Alternate Names",
      id: "Entity Alternate Names",
      accessorKey: "entity_alternate_names",
      enableSorting: false,
      cell: ({ row }) => (
        <ScrollColumn>
          {row.original.entity_alternate_names.map(({ name }) => (
            <li key={name}>{name}</li>
          ))}
        </ScrollColumn>
      ),
    },
    {
      header: "Notice Status",
      id: "Notice Status",
      accessorKey: "notice_status",
    },
    {
      header: "# Delivered",
      id: "# Delivered",
      accessorKey: "metrics.total_delivered",
    },
    {
      header: "# Opened",
      id: "# Opened",
      accessorKey: "metrics.total_opened",
    },
    {
      header: "# Responded",
      id: "# Responded",
      accessorKey: "metrics.total_responded",
    },
    {
      header: "First Delivered",
      id: "First Delivered",
      accessorKey: "metrics.first_delivered_at",
      cell: ({ row }) => {
        const value = row?.original?.metrics?.first_delivered_at;
        if (value !== undefined && value !== null) {
          return <RuvixxDate date={value} />;
        }
        return null;
      },
    },
    {
      header: "Last Delivered",
      id: "Last Delivered",
      accessorKey: "metrics.last_delivered_at",
      cell: ({ row }) => {
        const value = row?.original?.metrics?.last_delivered_at;
        if (value !== undefined && value !== null) {
          return <RuvixxDate date={value} />;
        }
        return null;
      },
    },
    {
      header: "First Opened",
      id: "First Opened",
      accessorKey: "metrics.first_opened_at",
      cell: ({ row }) => {
        const value = row?.original?.metrics?.first_opened_at;
        if (value !== undefined && value !== null) {
          return <RuvixxDate date={value} />;
        }
        return null;
      },
    },
    {
      header: "Last Opened",
      id: "Last Opened",
      accessorKey: "metrics.last_opened_at",
      cell: ({ row }) => {
        const value = row?.original?.metrics?.last_opened_at;
        if (value !== undefined && value !== null) {
          return <RuvixxDate date={value} />;
        }
        return null;
      },
    },
    {
      header: "Last Delivered Template",
      id: "Last Delivered Template",
      accessorKey: "metrics.last_delivered_template",
    },
    {
      header: "Responses",
      id: "Responses",
      accessorKey: "entity_id",
      enableSorting: false,
      cell: ({ row }) => {
        if (row.original.metrics.total_responded) {
          return (
            <FormResponsesListModal
              campaignId={parseInt(campaignId)}
              entityId={row.original.entity_id}
            />
          );
        }
        return "--";
      },
    },
    {
      header: "Last Response Date",
      id: "Last Response Date",
      accessorKey: "last_responded_at",
      cell: ({ row }) => {
        const value = row?.original?.last_responded_at;
        if (value !== undefined && value !== null) {
          return <RuvixxDate date={value} />;
        }
        return "--";
      },
    },
    {
      header: "Campaign Status",
      id: "Campaign Status",
      accessorKey: "campaign_status",
      cell: ({ row }) => (
        <CampaignStatusModal
          campaignStatus={row.original.campaign_status}
          campaignId={row.original.campaign_id || campaignId}
          entityId={row.original.entity_id}
          updateData={invalidateData}
          effectiveDate={row.original.effective_date}
        />
      ),
    },
    {
      header: "Effective Date",
      id: "Effective Date",
      accessorKey: "effective_date",
      cell: ({ row }) => {
        return row?.original?.effective_date || "N/A";
      },
    },
    {
      header: "Campaign Status Last Updated",
      id: "Campaign Status Last Updated",
      accessorKey: "status_last_updated_at",
      cell: ({ row }) => {
        const value = row?.original?.status_last_updated_at;
        if (value !== undefined && value !== null) {
          return <RuvixxDate date={value} />;
        }
        return "N/A";
      },
    },
    {
      header: "Converted Reason",
      id: "Converted Reason",
      accessorKey: "info.converted_reason",
    },
    {
      header: "Account Managers",
      id: "Account Managers",
      accessorKey: "account_managers",
      enableSorting: false,
      cell: ({ row }) => (
        <ScrollColumn>
          {row.original?.account_managers?.map(manager => (
            <li key={manager.user_id}>{manager.user_full_name}</li>
          ))}
        </ScrollColumn>
      ),
    },
    {
      header: "Assignee",
      id: "Assignee",
      accessorKey: "assigned_full_name",
      enableSorting: false,
    },
    {
      header: <div></div>,
      id: "Actions",
      accessorKey: "actions",
      enableSorting: false,
      enableResizing: false,
      size: 60,
      meta: {
        isDisplay: true,
      },
      cell: ({ row }) => (
        <CampaignTargetTableActions
          original={row.original}
          campaign={campaign}
          invalidateData={invalidateData}
          deleteCampaignTarget={deleteCampaignTarget}
        />
      ),
    },
  ];
};

export const getCampaignTargetsTableFilters = ({
  tags,
  campaignId,
  revenueOpportunityStatuses,
  campaignStatuses,
  templates,
}) => {
  return [
    {
      key: "entity_id",
      title: "Entity",
      type: "ruvixxSelect",
      operators: NEGATABLE_OPERATORS,
      queryFn: EntityService.getEntitiesForFilters,
      props: {
        campaign_ids: [campaignId],
      },
    },
    {
      key: "assigned_user_id",
      title: "Assignee",
      type: "ruvixxSelect",
      operators: NULLABLE_OPERATORS,
      queryFn: getUsersForFiltersQuery,
      props: {
        includeNoneOption: true,
      },
    },
    {
      key: "effective_date",
      title: "Effective Date",
      type: "dateRange",
    },
    {
      key: "notice_status",
      title: "Notice Status",
      type: "select",
      data: [
        { id: "delivered", name: "Delivered" },
        { id: "opened", name: "Opened" },
        { id: "failed", name: "Errored" },
      ],
    },
    {
      key: "last_delivered_template_id",
      title: "Last Delivered Template",
      type: "select",
      data: templates,
    },
    {
      key: "tag_id",
      title: "Tag",
      type: "tag",
      data: tags,
    },
    {
      key: "campaign_status",
      title: "Campaign Status",
      type: "select",
      data: campaignStatuses,
      props: {
        includeNoneOption: true,
      },
    },
    {
      key: "revenue_opportunity_status_id",
      title: "Revenue Opportunity Status",
      type: "select",
      data: revenueOpportunityStatuses,
      props: {
        includeNoneOption: true,
      },
    },
    {
      key: "account_manager_id",
      title: "Account Manager",
      type: "ruvixxSelect",
      operators: NULLABLE_OPERATORS,
      queryFn: getUsersForFiltersQuery,
      props: {
        includeNoneOption: true,
      },
    },
  ];
};
